import * as React from 'react'
import { Check, Loader2, Play, Users } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useQuery } from '@/hooks/use-query'
import type { Activity, TeamMember } from '@/lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ErrorNote, Loading } from '@/components/page'

/**
 * The step in front of every deck: who is going through this?
 *
 * Recording BEFORE rather than after is deliberate: afterwards everyone has
 * stood up and left, and the presenter is the last person who wants to do data
 * entry.
 *
 * **Attendance is offered, not required.** Starting with nobody selected opens
 * the deck and records a session with no attendees — which is what a rehearsal,
 * a solo read-through, or a deck shown to an audience who aren't on the team
 * actually is. This used to be impossible (the Start button was disabled at zero
 * selected), and the effect was that anyone wanting to look at their own slides
 * had to file false attendance for a room that didn't exist. Bad data is worse
 * than missing data.
 *
 * The roster is team_members, not profiles, so people who were invited but
 * haven't clicked their magic link yet are selectable. They're the most likely
 * attendees at a kickoff.
 */
export function PresentStartDialog({
  activity,
  onClose,
  onStarted,
}: {
  activity: Activity
  onClose: () => void
  /** Called with the new session's id once attendance is written. */
  onStarted: (sessionId: string) => void
}) {
  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  const [busy, setBusy] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)

  const { data, loading, error } = useQuery(async () => {
    const { data: rows, error: err } = await sb()
      .from('team_members')
      .select('*')
      .eq('team_id', activity.team_id)
      .eq('active', true)
      .order('display_name', { nullsFirst: false })
      .order('email')
    if (err) throw err
    return (rows ?? []) as TeamMember[]
  }, [activity.team_id])

  const members = data ?? []

  // Default to everyone. The common case is the whole team in a room, so
  // pre-selecting all makes the frequent path one click and leaves deselecting
  // as the exception.
  React.useEffect(() => {
    if (members.length > 0) setSelected(new Set(members.map((m) => m.id)))
  }, [data])

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function start() {
    setBusy(true)
    setSaveError(null)
    try {
      // team_id is re-derived from the activity by a trigger, so what we send
      // here is a formality the database doesn't trust.
      const { data: sess, error: sErr } = await sb()
        .from('presentation_sessions')
        .insert({ activity_id: activity.id, team_id: activity.team_id })
        .select('id')
        .single()
      if (sErr) throw sErr

      if (selected.size > 0) {
        const { error: aErr } = await sb()
          .from('presentation_attendees')
          .insert(
            [...selected].map((member_id) => ({
              session_id: sess.id as string,
              team_id: activity.team_id,
              member_id,
            })),
          )
        // The session row is already in, and the attendance the presenter asked
        // for is not — roll it back rather than silently recording a session for
        // nobody, which is now a legitimate state and so no longer looks wrong.
        if (aErr) {
          await sb().from('presentation_sessions').delete().eq('id', sess.id)
          throw aErr
        }
      }
      onStarted(sess.id as string)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            Who’s going through this?
          </DialogTitle>
          <DialogDescription>
            Everyone you pick is recorded as having been through “{activity.title}”. Pick nobody
            to just open the deck — a rehearsal or a read-through isn’t attendance.
            {/* Said before the fact rather than after: starting is what publishes
                the topic (0019), and a coach may be presenting from a hidden
                one without having thought about who else can now read it. */}
            {!activity.visible && ' Starting also makes this topic visible to the team.'}
          </DialogDescription>
        </DialogHeader>

        {/* One click to clear the room, because the alternative at zero-attendance
            was deselecting every name in turn. */}
        {members.length > 0 && (
          <div className="flex justify-end">
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={() =>
                setSelected(
                  selected.size === members.length ? new Set() : new Set(members.map((m) => m.id)),
                )
              }
            >
              {selected.size === members.length ? 'Select nobody' : 'Select everyone'}
            </Button>
          </div>
        )}

        <div className="my-3 max-h-[45vh] space-y-1 overflow-y-auto">
          {error && <ErrorNote error={error} />}
          {loading && !data ? (
            <Loading label="Loading the team…" />
          ) : members.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              This team has no active members yet — you can still present.
            </p>
          ) : (
            members.map((m) => {
              const on = selected.has(m.id)
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggle(m.id)}
                  aria-pressed={on}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                    on ? 'border-primary/40 bg-primary/5' : 'border-transparent hover:bg-muted',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                      on ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                    )}
                  >
                    {on && <Check className="h-3 w-3" />}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{m.display_name || m.email}</span>
                  {/* Signals that attendance is recorded but won't roll up into
                      this person's completion list until they sign in. */}
                  {!m.user_id && (
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      not signed in
                    </Badge>
                  )}
                </button>
              )
            })
          )}
        </div>

        {saveError && <ErrorNote error={saveError} />}

        <DialogFooter className="items-center gap-2 sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {selected.size === 0
              ? 'Nobody recorded — this run won’t count for anyone'
              : `${selected.size} of ${members.length} selected`}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            {/* Enabled at zero on purpose. The label carries the consequence, so
                nobody starts a recorded session thinking they're rehearsing or
                the other way round. */}
            <Button type="button" onClick={() => void start()} disabled={busy}>
              {busy ? <Loader2 className="animate-spin" /> : <Play />}
              {selected.size === 0 ? 'Start without recording' : 'Start'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
